import { useEffect, useState } from "react";
import { Users, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OPS } from "@/constants/testIds";
import { WM_TIMEOUT_MS, wmMessage } from "@/lib/wmError";
import { toLatinDigits } from "@/lib/wmDigits";

const C = OPS.wm;

/**
 * «عدد المستفيدين» — one value per branch-day. A second user's value
 * overwrites with an audit entry, and the header shows what is already
 * recorded so nobody types blind.
 */
export function BeneficiariesField({ day, branchId, date, onSaved }) {
    const { t, lang } = useI18n();
    const recorded = day?.day_meta?.beneficiaries_count;
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { setValue(""); }, [branchId, date]);

    const save = async () => {
        const n = Number(toLatinDigits(value));
        if (!value || Number.isNaN(n) || n < 0) return;
        setBusy(true);
        try {
            await api.put("/waste-meals/day-meta", {
                branch_id: branchId, date, type: "meals", beneficiaries_count: Math.trunc(n),
            }, { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_saved"));
            setValue("");
            onSaved && (await onSaved());
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-end gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] p-3">
            <div className="min-w-0 flex-1">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <Users className="h-3.5 w-3.5" />
                    {t("wm_beneficiaries")}
                    {recorded !== null && recorded !== undefined ? (
                        <span className="text-[color:var(--ink-faint)]">
                            · {t("wm_beneficiaries_recorded")}: <bdi dir="ltr">{recorded}</bdi>
                        </span>
                    ) : null}
                </label>
                <Input data-testid={C.beneficiaries} value={value} inputMode="numeric" dir="ltr"
                       onChange={(e) => setValue(toLatinDigits(e.target.value).replace(/[^\d]/g, ""))}
                       className="mt-1 h-10 tabular-nums" placeholder={t("wm_qty_placeholder")} />
            </div>
            <Button data-testid={C.beneficiariesSave} onClick={save} disabled={busy || !value}
                    variant="outline" className="h-10 gap-1.5">
                <Check className="h-4 w-4" />
                {t("wm_save")}
            </Button>
        </div>
    );
}
